/**
 * Google Analytics 4 Event Tracking Helper
 *
 * This module provides type-safe event tracking for GA4.
 * All events are sent to Google Analytics for conversion tracking and funnel analysis.
 */

// Event parameter types for better type safety
export interface EventParams {
  [key: string]: string | number | boolean | undefined;
}

export const ANALYTICS_CONSENT_KEY = 'combine-analytics-consent';
export const ANALYTICS_CONSENT_EVENT = 'combine-analytics-consent-change';
export function validAnalyticsMeasurementId(value: string | undefined): value is string {
  return !!value && value.startsWith('G-') && value.length > 2 && value.length <= 24 &&
    [...value.slice(2)].every(char => (char >= 'A' && char <= 'Z') || (char >= '0' && char <= '9'));
}
export const ANALYTICS_EVENT_NAMES = [
  'strava_connect_click', 'strava_connect_error', 'gpx_upload_start', 'gpx_upload_success',
  'gpx_upload_error', 'merge_candidates_detected', 'merge_preview_viewed', 'waypoint_edited',
  'merge_started', 'merge_success', 'merge_error', 'download_gpx_clicked',
  'upload_to_strava_clicked', 'upload_to_strava_success', 'upload_to_strava_error',
  'homepage_video_played', 'guide_clicked', 'faq_expanded', 'social_share',
  'conversion', 'funnel_complete', 'app_error', 'api_error',
] as const;
const EVENTS = new Set<string>(ANALYTICS_EVENT_NAMES);
export const ANALYTICS_ERROR_CATEGORIES = [
  'unknown', 'invalid_extension', 'unsupported_fit', 'no_points', 'duplicate', 'invalid_xml',
  'invalid_coordinates', 'empty_file', 'file_too_large', 'strava_access_denied',
  'strava_profile_forbidden', 'configuration', 'network', 'rate_limit', 'unauthorized',
  'render', 'unhandled', 'unhandled_rejection',
] as const;
const ERROR_CATEGORIES = new Set<string>(ANALYTICS_ERROR_CATEGORIES);
function isGAAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.combineAnalytics === 'function' &&
      window.localStorage.getItem(ANALYTICS_CONSENT_KEY) === 'granted';
  } catch { return false; }
}
export const ANALYTICS_SAFE_PATHS = ['/', '/dashboard', '/success', '/privacy', '/terms', '/about', '/guides', '/merge', '/other', '/support'] as const;
export interface AnalyticsMessage {
  event: 'analytics_event';
  name: string;
  category?: string;
  safePath?: string;
}
export function sanitizeAnalyticsMessage(value: unknown): AnalyticsMessage | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<AnalyticsMessage>;
  if (candidate.event !== 'analytics_event' || typeof candidate.name !== 'string' || candidate.name.length > 64) return null;
  if (candidate.name === 'page_view') {
    return typeof candidate.safePath === 'string' && (ANALYTICS_SAFE_PATHS as readonly string[]).includes(candidate.safePath)
      ? { event: 'analytics_event', name: 'page_view', safePath: candidate.safePath } : null;
  }
  if (!EVENTS.has(candidate.name)) return null;
  return candidate.name.endsWith('_error')
    ? { event: 'analytics_event', name: candidate.name, category: typeof candidate.category === 'string' && ERROR_CATEGORIES.has(candidate.category) ? candidate.category : 'unknown' }
    : { event: 'analytics_event', name: candidate.name };
}
export function safeAnalyticsPath(path: string): string {
  if ((ANALYTICS_SAFE_PATHS as readonly string[]).includes(path)) return path;
  if (path.startsWith('/merge/')) return '/merge';
  if (path.startsWith('/guides/')) return '/guides';
  return '/other';
}
export const trackEvent = (eventName: string, params?: EventParams): void => {
  if (!isGAAvailable()) return;
  const message = sanitizeAnalyticsMessage({ event: 'analytics_event', name: eventName, category: params?.error_type });
  if (message) window.combineAnalytics?.(message);
};
export const trackPageView = (path: string): void => {
  if (!isGAAvailable()) return;
  window.combineAnalytics?.({ event: 'analytics_event', name: 'page_view', safePath: safeAnalyticsPath(path) });
};
// ========================================
// STRAVA AUTHENTICATION EVENTS
// ========================================

/**
 * User clicks "Connect with Strava" button
 */
export const trackStravaConnectClick = (): void => {
  trackEvent('strava_connect_click', {
    button_location: 'homepage',
  });
};

/**
 * Strava authentication fails.
 * `error_type` is a stable category for a GA4 custom dimension.
 * `error_message` is the short user-facing sentence.
 */
export const trackStravaConnectError = (
  errorType: string,
  errorMessage?: string
): void => {
  trackEvent('strava_connect_error', {
    error_type: errorType,
    error_message: errorMessage?.slice(0, 100),
  });
};

// ========================================
// GPX UPLOAD EVENTS
// ========================================

/**
 * User initiates GPX file upload
 */
export const trackGPXUploadStart = (fileCount: number): void => {
  trackEvent('gpx_upload_start', {
    file_count: fileCount,
  });
};

/**
 * GPX upload completes successfully
 */
export const trackGPXUploadSuccess = (
  fileCount: number,
  totalActivities: number
): void => {
  trackEvent('gpx_upload_success', {
    file_count: fileCount,
    total_activities: totalActivities,
  });
};

/**
 * GPX upload fails.
 * `error_type` is one of the stable codes from `GpxUploadErrorCode`
 * (invalid_extension, unsupported_fit, no_points, duplicate, ...).
 */
export const trackGPXUploadError = (
  errorType: string,
  errorMessage?: string
): void => {
  trackEvent('gpx_upload_error', {
    error_type: errorType,
    error_message: errorMessage?.slice(0, 100),
  });
};

// ========================================
// MERGE DETECTION & PREVIEW EVENTS
// ========================================

/**
 * Merge candidates are detected
 */
export const trackMergeCandidatesDetected = (
  candidateCount: number,
  confidenceLevel?: 'high' | 'medium' | 'low'
): void => {
  trackEvent('merge_candidates_detected', {
    candidate_count: candidateCount,
    confidence_level: confidenceLevel,
  });
};

/**
 * User views merge preview
 */
export const trackMergePreviewViewed = (
  activityCount: number,
  totalDistance?: number
): void => {
  trackEvent('merge_preview_viewed', {
    activity_count: activityCount,
    total_distance: totalDistance,
  });
};

/**
 * User edits waypoints on the map
 */
export const trackWaypointEdited = (
  waypointIndex: number,
  action: 'add' | 'remove' | 'move'
): void => {
  trackEvent('waypoint_edited', {
    waypoint_index: waypointIndex,
    action,
  });
};

// ========================================
// MERGE COMPLETION EVENTS
// ========================================

/**
 * User initiates merge process
 */
export const trackMergeStarted = (
  activityCount: number,
  method: 'strava' | 'gpx'
): void => {
  trackEvent('merge_started', {
    activity_count: activityCount,
    method,
  });
};

/**
 * Merge completes successfully
 */
export const trackMergeSuccess = (
  activityCount: number,
  totalPoints: number,
  interpolatedPoints: number,
  processingTime: number
): void => {
  trackEvent('merge_success', {
    activity_count: activityCount,
    total_points: totalPoints,
    interpolated_points: interpolatedPoints,
    processing_time_ms: processingTime,
  });
};

/**
 * Merge fails
 */
export const trackMergeError = (
  errorMessage?: string,
  errorType?: string
): void => {
  trackEvent('merge_error', {
    error_message: errorMessage,
    error_type: errorType,
  });
};

// ========================================
// DOWNLOAD & UPLOAD EVENTS
// ========================================

/**
 * User downloads merged GPX file
 */
export const trackDownloadGPX = (
  fileSize?: number,
  activityCount?: number
): void => {
  trackEvent('download_gpx_clicked', {
    file_size_kb: fileSize,
    activity_count: activityCount,
  });
};

/**
 * User uploads merged activity to Strava
 */
export const trackUploadToStrava = (activityType?: string): void => {
  trackEvent('upload_to_strava_clicked', {
    activity_type: activityType,
  });
};

/**
 * Upload to Strava succeeds
 */
export const trackUploadToStravaSuccess = (activityId?: string): void => {
  trackEvent('upload_to_strava_success', {
    activity_id: activityId,
  });
};

/**
 * Upload to Strava fails
 */
export const trackUploadToStravaError = (errorMessage?: string): void => {
  trackEvent('upload_to_strava_error', {
    error_message: errorMessage,
  });
};

// ========================================
// USER ENGAGEMENT EVENTS
// ========================================

/**
 * User plays demo video on homepage
 */
export const trackHomepageVideoPlayed = (): void => {
  trackEvent('homepage_video_played', {
    video_location: 'hero_section',
  });
};

/**
 * User clicks on a guide link
 */
export const trackGuideClicked = (guideName: string): void => {
  trackEvent('guide_clicked', {
    guide_name: guideName,
  });
};

/**
 * User expands FAQ item
 */
export const trackFAQExpanded = (question: string): void => {
  trackEvent('faq_expanded', {
    question,
  });
};

/**
 * User shares on social media
 */
export const trackSocialShare = (platform: string): void => {
  trackEvent('social_share', {
    platform,
  });
};

// ========================================
// CONVERSION TRACKING
// ========================================

/**
 * Track successful conversion (merge completion is the main goal)
 */
export const trackConversion = (
  conversionType: 'merge_completed' | 'strava_connected' | 'gpx_uploaded',
  value?: number
): void => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value,
  });
};

/**
 * Track user completing the full funnel
 */
export const trackFunnelComplete = (
  startTime: number,
  method: 'strava' | 'gpx'
): void => {
  const completionTime = Date.now() - startTime;

  trackEvent('funnel_complete', {
    completion_time_ms: completionTime,
    method,
  });
};

// ========================================
// ERROR TRACKING
// ========================================

/**
 * Track application errors
 */
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorStack?: string
): void => {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 150), // Limit message length
    has_stack: !!errorStack,
  });
};

/**
 * Track API errors
 */
export const trackAPIError = (
  endpoint: string,
  statusCode: number,
  errorMessage?: string
): void => {
  trackEvent('api_error', {
    endpoint,
    status_code: statusCode,
    error_message: errorMessage,
  });
};

// TypeScript declaration for gtag
declare global {
  interface Window {
    combineAnalytics?: (message: AnalyticsMessage) => void;
  }
}

const analytics = {
  trackEvent,
  trackPageView,
  trackStravaConnectClick,
  trackStravaConnectError,
  trackGPXUploadStart,
  trackGPXUploadSuccess,
  trackGPXUploadError,
  trackMergeCandidatesDetected,
  trackMergePreviewViewed,
  trackWaypointEdited,
  trackMergeStarted,
  trackMergeSuccess,
  trackMergeError,
  trackDownloadGPX,
  trackUploadToStrava,
  trackUploadToStravaSuccess,
  trackUploadToStravaError,
  trackHomepageVideoPlayed,
  trackGuideClicked,
  trackFAQExpanded,
  trackSocialShare,
  trackConversion,
  trackFunnelComplete,
  trackError,
  trackAPIError,
};

export default analytics;
